/**
 * Checklist de Frota no portal: continuar o que ficou pela metade e ver os
 * últimos. A lista é em cartões — tabela de 7 colunas não se lê no pátio.
 */
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceRunDto } from "@bi/types";
import { api } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { IconClipboard, IconChevronRight } from "../../components/icons";
import { KIND_LABEL, MOVEMENT_LABEL, RUN_STATUS } from "../../features/maintenance/labels";
import { portalPaths } from "../../features/portal/portal-nav";
import { useCapabilities } from "../../features/maintenance/useCapabilities";

const FILTROS = [
  { value: "", label: "Todos" },
  { value: "in_progress", label: "Em andamento" },
  { value: "completed", label: "Concluídos" },
];

export function PortalChecklistsPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const plate = params.get("plate") ?? "";
  const { can } = useCapabilities();

  const lista = useQuery({
    queryKey: ["maintenance", "runs", "portal", status, plate],
    queryFn: () => {
      const q = new URLSearchParams({ limit: "30" });
      if (status) q.set("status", status);
      if (plate) q.set("plate", plate);
      return api.get<{ rows: MaintenanceRunDto[]; total: number }>(`/maintenance/runs?${q}`);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-gray-900">Checklist de Frota</h1>
        {can("checklist.execute") && (
          <Link to={portalPaths.newChecklist()} className="btn-primary min-h-[44px]">Iniciar</Link>
        )}
      </div>

      <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {FILTROS.map((f) => (
          <button
            key={f.value}
            onClick={() => {
              const p = new URLSearchParams(params);
              if (f.value) p.set("status", f.value); else p.delete("status");
              setParams(p, { replace: true });
            }}
            className={`flex-shrink-0 min-h-[40px] px-3 rounded-lg border text-sm ${
              status === f.value ? "bg-tm-500 text-white border-tm-500" : "bg-white text-gray-600 border-surface-border"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {plate && (
        <p className="text-sm text-gray-500">
          Filtrando pela placa <span className="font-mono">{plate}</span>{" "}
          <button className="text-tm-700 hover:underline" onClick={() => { const p = new URLSearchParams(params); p.delete("plate"); setParams(p, { replace: true }); }}>
            limpar
          </button>
        </p>
      )}

      {lista.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : (lista.data?.rows.length ?? 0) === 0 ? (
        <div className="card">
          <EmptyState
            icon={<IconClipboard className="w-6 h-6" />}
            title="Nenhum checklist por aqui"
            description="Comece um checklist de entrega ou devolução do conjunto."
            action={can("checklist.execute") ? <Link className="btn-primary" to={portalPaths.newChecklist()}>Iniciar checklist</Link> : undefined}
          />
        </div>
      ) : (
        <div className="card divide-y divide-surface-border">
          {lista.data!.rows.map((r) => (
            <Link key={r.id} to={portalPaths.run(r.id)} className="flex items-center gap-3 px-4 py-3 min-h-[64px] hover:bg-surface-muted">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {r.template?.name ?? KIND_LABEL[r.kind]}
                  {r.movement && r.kind !== "delivery" && r.kind !== "return_delivery" ? ` · ${MOVEMENT_LABEL[r.movement]}` : ""}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  <span className="font-mono">{r.vehiclePlate}</span>
                  {r.trailerPlate && <span className="font-mono"> + {r.trailerPlate}</span>}
                  {r.driverName ? ` · ${r.driverName}` : ""}
                  {r.inspectorName ? ` · por ${r.inspectorName}` : ""}
                  {` · ${new Date(r.startedAt).toLocaleDateString("pt-BR")}`}
                  {r.odometerKm != null ? ` · ${fmtNumber(r.odometerKm)} km` : ""}
                </p>
                {(r._count?.nonConformities ?? 0) > 0 && (
                  <p className="text-xs text-danger-600 mt-0.5">{r._count!.nonConformities} ocorrência(s)</p>
                )}
              </div>
              <span className={RUN_STATUS[r.status].badge}>{RUN_STATUS[r.status].label}</span>
              <IconChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
